import React, {useMemo} from 'react'
import {
    Box,
    Divider,
    Flex,
    Image,
    Text,
    useMediaQuery
} from '@salesforce/retail-react-app/app/components/shared/ui'
import PropTypes from 'prop-types'
import {colors} from '../../constants'
import {contentfulMediaFormatter} from '../../utils'
import {documentToReactComponents} from '@contentful/rich-text-react-renderer'
import {options} from '../rich-text/Options'
import './bookServicesCard.scss'
import Link from '../link'
import {FormattedNumber, useIntl} from 'react-intl'
import {useCurrency} from '../../hooks/use-currency'

export const BookServiceCard = ({data}) => {
    const {currency: activeCurrency} = useCurrency()
    const {formatMessage} = useIntl()
    const [isDesktop] = useMediaQuery('(min-width: 992px)')

    const cta = useMemo(() => data?.cta?.fields, [data])
    const asset = useMemo(() => {
        const media = data?.media?.fields
            ? contentfulMediaFormatter(data?.media)
            : data?.media

        return !isDesktop && media?.mobile?.url ? media?.mobile : media?.desktop
    }, [data, isDesktop])

    const textStyles = {
        fontFamily: 'Roboto',
        fontSize: '12px',
        fontWeight: '500',
        lineHeight: '20px',
        textTransform: 'uppercase'
    }

    const btnStyles = {
        fontWeight: 500,
        fontSize: '16px',
        minWidth: '80px',
        padding: '8px 16px',
        textAlign: 'center',
        color: colors.black,
        borderRadius: '34px',
        lineHeight: 'normal',
        height: 'fit-content',
        fontFamily: 'Raleway',
        textTransform: 'uppercase'
    }

    return (
        <Flex
            width="100%"
            alignItems="center"
            borderRadius="32px"
            justifyContent="space-between"
            gap={{base: '16px', lg: 'unset'}}
            flexFlow={{base: 'column', lg: 'row'}}
            backgroundColor={colors.footerBgGradient}
            p={{base: '24px', lg: '12px 12px 12px 32px'}}
        >
            <Flex gap="16px" flexFlow="column">
                <Flex flexFlow="column" gap="17px">
                    <Text
                        fontSize="20px"
                        fontWeight="500"
                        lineHeight="24px"
                        fontFamily="Raleway"
                        textTransform="uppercase"
                    >
                        {data?.title}
                    </Text>
                    <Flex gap="12px" alignItems="center">
                        {data?.duration && (
                            <Text {...textStyles}>{data?.duration}</Text>
                        )}
                        {data?.price && data?.duration && (
                            <Divider
                                my="7px"
                                height="20px"
                                color="black"
                                fontWeight="400"
                                border="1px solid black"
                                orientation="vertical"
                            />
                        )}
                        {data?.price && (
                            <Text {...textStyles}>
                                ${Math.trunc(data?.price ?? 0)}
                            </Text>
                        )}
                    </Flex>
                    {data?.description && (
                        <Box className="book-description" maxWidth="470px">
                            {documentToReactComponents(
                                data?.description,
                                options({
                                    entries: {inline: []},
                                    assets: {block: []}
                                })
                            )}
                        </Box>
                    )}
                </Flex>
                {(cta || data?.showAllServicesButton) && (
                    <Flex width="100%" gap="11px">
                        {cta && (
                            <Link
                                {...cta}
                                buttonStyles={{
                                    ...btnStyles,
                                    backgroundColor: colors.quilGray
                                }}
                            />
                        )}
                        {data?.showAllServicesButton && (
                            <Link
                                buttonStyles={{
                                    ...btnStyles,
                                    background: 'unset',
                                    backgroundColor: colors.white,
                                    _hover: {
                                        backgroundColor: colors.quilGray,
                                        fontWeight: 500
                                    }
                                }}
                                text={formatMessage({
                                    id: 'book_service_card.view_all_services',
                                    defaultMessage: 'View All Services'
                                })}
                                url="/services"
                            />
                        )}
                    </Flex>
                )}
            </Flex>
            {asset && (
                <Box
                    borderRadius="24px"
                    maxWidth={{base: 'unset', lg: '508px'}}
                >
                    <Image
                        src={asset?.url}
                        alt={data?.title}
                        objectFit="cover"
                    />
                </Box>
            )}
        </Flex>
    )
}

BookServiceCard.displayName = 'BookServiceCard'

BookServiceCard.propTypes = {
    data: PropTypes.shape({
        title: PropTypes.string.isRequired,
        price: PropTypes.string,
        duration: PropTypes.string,
        description: PropTypes.obj,
        media: PropTypes.obj,
        cta: PropTypes.obj,
        showAllServicesButton: PropTypes.bool
    })
}

export default BookServiceCard
